import type { Locale } from '@tuic/i18n';
import { CacheTag } from '../../utils';
import { remember, serializeDataToCacheKey } from '@tuic/cache';
import { client } from '../../generated/pcl/client.gen';
import { logger } from '@tuic/logger';
import { VenueItem } from '../../generated/pcl';

/**
 * Fetches the venue details from the PCL API.
 */
export const getVenueDetails = async (
  venueId: string,
  locale: Locale,
): Promise<VenueItem[] | null> => {
  const { data, error } = await client.get<VenueItem[], unknown, false>({
    url: '/venue/{venueId}',
    path: {
      venueId,
    },
    query: {
      locale,
    },
  });

  if (error) {
    logger.error(
      `Could not fetch venue details. Venue: ${venueId}, Locale: ${locale}`,
    );
    logger.error(error);

    return null;
  }

  if (typeof data === 'undefined') {
    return null;
  }

  return data;
};

/**
 * Fetches the venue details from the PCL API.
 */
export const cachedGetVenueDetails = (
  venueId: string,
  locale: Locale,
): Promise<VenueItem[] | null> =>
  remember(
    serializeDataToCacheKey([CacheTag.pcl, 'getVenueDetails', locale, venueId]),
    () => getVenueDetails(venueId, locale),
    {
      tags: [CacheTag.pcl, CacheTag.venueResource, CacheTag.getVenueDetails],
      ttl: 600,
    },
  );
